import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';

import type { RadiobuttonItemInfo } from 'webapp-types';
import { sanitize } from 'webapp/utils/html';

import themed from 'webapp/decorators/themed';
import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonSingleComponent from 'webapp/components/form/radiobutton-single';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

export type { RadiobuttonItemInfo };

export interface Args {
	name: string;
	value: AnySimpleValue;
	selected: AnySimpleValue;
	label: string;
	onChange: (newSelected: AnySimpleValue) => void;
	isDisabled?: boolean;
	isGroupDisabled?: boolean;
	highlightText?: string;
	label2?: string;
	tooltipText?: string;
	item?: RadiobuttonItemInfo;
	theme?: string;
	size?: string;
}

@themed()
export default class FormRadiobuttonComponent extends Component<Args> {
	guid = guidFor(this);

	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	get id() {
		return `${this.args.name || 'radiobutton'}-${this.guid}`;
	}

	get isDisabled() {
		return this.args.isDisabled || this.args.isGroupDisabled;
	}

	get hasTooltip() {
		return this.args.item?.tooltip;
	}

	get hasLinkOrTooltip() {
		return this.hasTooltip || this.args.item?.link;
	}

	get iconName(): IconName {
		return this.args.item?.tooltipIcon || 'Question';
	}

	get valueEscaped() {
		return String(this.args.value ?? '').replace(/[^a-zA-Z0-9]+/g, '');
	}

	get valueClass() {
		return `val-${this.args.name}-${this.valueEscaped}`;
	}

	get isChecked() {
		return (
			this.args.value === this.args.selected ||
			(typeof this.args.value !== 'number' && !this.args.value && !this.args.selected)
		);
	}

	get sanitizedLabel() {
		// must sanitize bcos we allow complex html here, and we use tripe curlies in the template
		return sanitize(this.args.label || '');
	}

	@action
	doLinkAction(e: PointerEvent) {
		this.args.item?.link?.action();
		e.preventDefault();
	}
	<template>
		<span
			class={{joinClasses
				(array
					"radiobutton"
					this.themeClass
					this.sizeClass
					this.valueClass
					(if this.hasTooltip "hasTooltip")
					(if this.isDisabled "disabled")
					(if this.hasLinkOrTooltip "hasLinkOrTooltip")
				)
			}}
		>
			<FormRadiobuttonSingleComponent
				@id={{this.id}}
				@name={{@name}}
				@value={{@value}}
				@selected={{@selected}}
				@isDisabled={{this.isDisabled}}
				@onChange={{@onChange}}
			/>

			<label for={{this.id}}>
				{{#if @item.icon}}
					<CommonUiIconComponent
						@name={{@item.icon}}
						@size={{if @item.iconSize @item.iconSize "md"}}
					/>
				{{/if}}
				<span class="text">
					{{{highlightResult text=this.sanitizedLabel match=@highlightText}}}
				</span>
				{{#if @label2}}
					<span class="label2">{{@label2}}</span>
				{{/if}}
				{{#if @tooltipText}}
					<ToolTipComponent @text={{@tooltipText}} />
				{{/if}}
			</label>

			{{#if @item.link.href}}
				<a
					href={{@item.link.href}}
					style={{@item.link.style}}
					target={{@item.link.target}}
				>{{@item.link.label}}
				</a>
			{{else if @item.link.action}}
				<a
					role="button"
					class="link"
					{{on "click" this.doLinkAction}}
					style={{@item.link.style}}
				>{{@item.link.label}}
				</a>
			{{else if @item.tooltip}}
				<ToolTipWithIconComponent @text={{@item.tooltip}} @iconName={{this.iconName}} />
			{{/if}}

		</span>
	</template>
}
